import React, { useState } from 'react';
import { StudentData, FilterState } from '../utils/types';
import { viewResume } from '../utils/dataUtils';

interface StudentCardProps {
  student: StudentData;
  darkMode: boolean;
  isShortlisted: boolean;
  onToggleShortlist: (student: StudentData) => void;
}

const StudentCard: React.FC<StudentCardProps> = ({ student, darkMode, isShortlisted, onToggleShortlist }) => {
  const handleViewResume = () => {
    if (student.Internal_ID) {
      window.open(`/byid/${student.Internal_ID}.pdf`, '_blank');
    }
  };
  return (
    <div className={`p-4 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
      <div
        className={`p-4 rounded-lg shadow ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
      >
        <div className="flex items-center space-x-4 mb-4">
          <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
            {student.Internal_ID ? (
              <img
                src={`/headshots/${student.Internal_ID}.png`}
                alt={`${student.name}'s headshot`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // If image fails to load, show a default avatar
                  e.currentTarget.src = 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y';
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-300">
                <span className="text-gray-500">No Photo</span>
              </div>
            )}
          </div>
          <div>
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-black'}`}>
              {student.name}
            </h3>
            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {student.university}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {student.major} - Year {student.year}
            </p>
            <div className="flex items-center space-x-2 mt-1">
              <span className={`px-2 py-1 rounded text-sm ${
                student.gpa >= 3.5 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' :
                student.gpa >= 3.0 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100' :
                'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100'
              }`}>
                GPA: {student.gpa.toFixed(2)}
              </span>
              <button
                onClick={() => onToggleShortlist(student)}
                title={isShortlisted ? 'Remove from Shortlist' : 'Add to Shortlist'}
                className="ml-2 text-xl focus:outline-none"
                style={{ color: isShortlisted ? '#f59e42' : '#bbb' }}
              >
                {isShortlisted ? '★' : '☆'}
              </button>
            </div>
            <div className="mt-2">
              {student.Internal_ID ? (
                <button
                  onClick={handleViewResume}
                  className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                >
                  View Resume
                </button>
              ) : (
                <span className="text-gray-500">No resume on file.</span>
              )}
            </div>
          </div>
        </div>
        
        <div className="mt-4">
          {/*
          <div className="mb-4">
            <h4 className="font-semibold mb-2">Skills</h4>
            <div className="flex flex-wrap gap-2">
              {student.skills.map((skill, index) => (
                <span 
                  key={index}
                  className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm dark:bg-blue-900 dark:text-blue-100"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
          */}
        </div>
      </div>
    </div>
  );
};

interface StudentProfilesProps {
  data: StudentData[];
  darkMode: boolean;
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  shortlist: StudentData[];
  addToShortlist: (student: StudentData) => void;
  removeFromShortlist: (student: StudentData) => void;
}

const StudentProfiles: React.FC<StudentProfilesProps> = ({ 
  data, 
  darkMode,
  filters,
  setFilters,
  shortlist,
  addToShortlist,
  removeFromShortlist
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const studentsPerPage = 10;

  // Sort students by institution and then by first name
  const sortedStudents = [...data].sort((a, b) => {
    // First compare by institution
    const institutionCompare = a.university.localeCompare(b.university);
    if (institutionCompare !== 0) return institutionCompare;
    
    // If same institution, compare by first name
    const aFirstName = a.name.split(' ')[0];
    const bFirstName = b.name.split(' ')[0];
    return aFirstName.localeCompare(bFirstName);
  });

  // Pagination logic
  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = sortedStudents.slice(indexOfFirstStudent, indexOfLastStudent);

  const totalPages = Math.ceil(sortedStudents.length / studentsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const isShortlisted = (student: StudentData) =>
    shortlist.some(s => s.Internal_ID === student.Internal_ID);

  const handleToggleShortlist = (student: StudentData) => {
    if (isShortlisted(student)) {
      removeFromShortlist(student);
    } else {
      addToShortlist(student);
    }
  };

  return (
    <div className={`p-4 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'}`}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">👨‍🎓 Student Profiles</h2>
        <div className="text-sm">
          Total Students: {data.length}
        </div>
      </div>

      {/* Student Cards */}
      <div>
        {currentStudents.map((student) => (
          <StudentCard 
            key={student.phone} 
            student={student} 
            darkMode={darkMode} 
            isShortlisted={isShortlisted(student)}
            onToggleShortlist={handleToggleShortlist}
          />
        ))}
      </div>

      {/* Pagination */}
      <div className="flex justify-center items-center space-x-2 mt-4">
        <button 
          onClick={() => paginate(currentPage - 1)} 
          disabled={currentPage === 1}
          className={`
            px-4 py-2 rounded
            ${currentPage === 1 
              ? 'bg-gray-300 cursor-not-allowed' 
              : 'bg-blue-500 text-white hover:bg-blue-600'
            }
          `}
        >
          Previous
        </button>

        <span className="text-sm">
          Page {currentPage} of {totalPages}
        </span>

        <button 
          onClick={() => paginate(currentPage + 1)} 
          disabled={currentPage === totalPages}
          className={`
            px-4 py-2 rounded
            ${currentPage === totalPages 
              ? 'bg-gray-300 cursor-not-allowed' 
              : 'bg-blue-500 text-white hover:bg-blue-600'
            }
          `}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default StudentProfiles;